import { getComputedDisplayName } from "../common";
import { ReactElement, useMemo } from "react";
import { ExchangeContactDetailsModel } from "client/Api/Email/contacts";
import { isNotNil, isNotNilOrEmpty } from "client/Misc/utils";
import { PersonalInformationPage } from "client/Features/PersonalInformationPage/PersonalInformationPage";
import { GeneralInformationSection } from "./Sections/GeneralInformationSection";
import { useUserTimezone } from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";
import { AddressSection } from "./Sections/AddressSection";
import { MultilineTextSection } from "./Sections/MultilineTextSection";
import { localize } from "client_legacy/ndaw/culture";
import { CommunicationButtons } from "../CommunicationButtons/CommunicationButtons";
import { DateOnly } from "client/DateAndTime/dateOnly";
import { parseDtoDateOnlyIgnoringOffset } from "client/DateAndTime/dateUtils";

export type DetailsProps = {
	contact: ExchangeContactDetailsModel;
};

export const DetailsForExchange = (props: DetailsProps): ReactElement => {
	const {
		contact
	} = props;

	const {
		photoUrl,
		company,
		department,
		officeLocation,
		jobTitle,
		birthday: birthdayAsString,
		emailAddress: emailAddress1,
		emailAddress2,
		emailAddress3,
		mobilePhone,
		mobilePhone2,
		homePhone,
		homePhone2,
		businessPhone,
		businessPhone2,
		homeAddress,
		businessAddress,
		otherAddress,
		notes
	} = contact;

	const userTimezone: TimeZone = useUserTimezone();

	const mainEmailAddress: string | null = getMostDominantEmailAddress(contact);
	const mainPhoneNumber: string | null = getMostDominantPhoneNumber(contact);

	const {
		computedDisplayName,
		initials
	} = getComputedDisplayName(contact);

	const info = useMemo(
		(): string => {
			const sections: string[] = [
				jobTitle,
				department,
				officeLocation,
				company
			]
				.filter(isNotNilOrEmpty);

			return sections.join(" | ");
		},
		[
			department,
			jobTitle,
			company,
			officeLocation
		]
	);

	const birthday = useMemo(
		(): DateOnly | null => {
			return parseDtoDateOnlyIgnoringOffset(birthdayAsString);
		},
		[birthdayAsString]
	);

	return (
		<PersonalInformationPage
			trackingId="exchange-contact"
		>
			<PersonalInformationPage.Banner
				trackingId="banner"
				fullName={computedDisplayName}
				avatarUrl={photoUrl ?? undefined}
				avatarPlaceholder={{ kind: "initials", initials }}
				info={info}
				actions={(
					<CommunicationButtons
						emailAddress={mainEmailAddress}
						phoneNumber={mainPhoneNumber}
						variant="details"
					/>
				)}
			/>
			<GeneralInformationSection
				birthday={birthday}
				emptyBehavior="not-render"
				emailAddresses={
					[
						emailAddress1,
						emailAddress2,
						emailAddress3
					]
						.filter(isNotNil)
				}
				phoneNumbers={{
					mobilePhone,
					mobilePhone2,
					homePhone,
					homePhone2,
					businessPhone,
					businessPhone2
				}}
				timezone={userTimezone}
				areContactLinksEnabled
				isCopyToClipboardEnabled
				shouldShowBirthYear
			/>
			<AddressSection
				homeAddress={homeAddress}
				businessAddress={businessAddress}
				otherAddress={otherAddress}
				emptyBehavior="not-render"
			/>
			<MultilineTextSection
				label={localize("_Notes")}
				trackingId="notes"
				text={notes}
				emptyBehavior="not-render"
			/>
		</PersonalInformationPage>
	);
};
DetailsForExchange.displayName="DetailsForExchange";

function getMostDominantEmailAddress(contact: ExchangeContactDetailsModel): string | null {

	const {
		emailAddress,
		emailAddress2,
		emailAddress3
	} = contact;

	return getFirstWithActualValue(emailAddress, emailAddress2, emailAddress3);
}

function getMostDominantPhoneNumber(contact: ExchangeContactDetailsModel): string | null {
	const {
		businessPhone,
		mobilePhone,
		homePhone,
	} = contact;

	return getFirstWithActualValue(businessPhone, mobilePhone, homePhone);
}

function getFirstWithActualValue(...values: (string | nil)[]): string | null {
	for (const value of values) {
		if (!isNotNil(value)) {
			continue;
		}

		const consistsOnlyOutOfWhiteSpaces: boolean = !value.trim();

		if (!consistsOnlyOutOfWhiteSpaces) {
			return value;
		}
	}
	return null;
}